import {
  AlertVariant,
  Bullseye,
  Button,
  DatePicker,
  EmptyState,
  EmptyStateBody,
  Flex,
  FlexItem,
  Label,
  MenuToggle,
  MenuToggleElement,
  PageSection,
  SearchInput,
  Select,
  SelectOption,
  Spinner,
  Title,
  Toolbar,
  ToolbarContent,
  ToolbarGroup,
  ToolbarItem,
  Tooltip
} from '@patternfly/react-core';
import { HistoryIcon } from '@patternfly/react-icons';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { HistoryApi } from '@app/api';
import { useNotification } from '@app/AppLayout/NotificationContext';
import { ChangeAuthor } from '@app/components/ChangeAuthor';
import { DataLoadError } from '@app/components/DataLoadError';
import { HistoryPager } from '@app/components/HistoryPager';
import { hasTarget, targetFor } from '@app/Activity/recordPaths';
import { AuditableEntityView, FeedEntry, HistoryFeed, TouchedEntity } from '@app/model/History';
import { ApiError, toApiError } from '@app/utils/ApiError';
import { HISTORY_PAGE_SIZE } from '@app/utils/config';
import { dateFormater, ddMMyyyyHHmmFormat, ddMMyyyyHHmmTzFormat, ddMMyyyyParse } from '@app/utils/functions';

/** The filters, held together because a change to any of them sends the reader back to page 0. */
interface FeedFilters {
  username: string;
  entity: string;
  /**
   * The dates as typed, "dd/MM/yyyy", and not as parsed dates.
   *
   * A DatePicker bound to the parsed value rewrites the field on every keystroke - the first digit
   * parses to *something*, and formatting that back replaces what is being typed - so a date can
   * only be picked from the calendar and never typed. Held as text, the field is left alone and a
   * half typed date simply does not filter. See instantRange for where it becomes an instant.
   */
  from: string;
  to: string;
}

const NO_FILTERS: FeedFilters = { username: '', entity: '', from: '', to: '' };

/**
 * What changed recently across everything, from GET /history/feed.
 *
 * A different question from a record's own change log: not "what happened to this class" but "what
 * happened this morning, and who did it". The two read the same revisions - `revinfo` and the audit
 * tables - from opposite ends, so a revision found here is followed into the record's own log.
 *
 * Open to any authenticated user, which is why it sits outside Administration: the feed reports
 * revisions to entity types the caller may not read as well, but for those it reports the type name
 * alone with no ids and nothing to follow. That discloses less than the entity update socket
 * already broadcasts, and omitting them would present an incomplete timeline as a complete one.
 */
const Activity: React.FunctionComponent = () => {
  const [feed, setFeed] = React.useState<HistoryFeed | null>(null);
  const [entities, setEntities] = React.useState<AuditableEntityView[]>([]);
  const [filters, setFilters] = React.useState<FeedFilters>(NO_FILTERS);
  const [page, setPage] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<ApiError | null>(null);

  /**
   * The entity picker offers what this caller may read the history of, asked of the service rather
   * than listed here - so an entity registered on that side appears in the filter on its own, and
   * one they may not read is never offered a filter that would answer 403.
   */
  React.useEffect(() => {
    void (async () => {
      try {
        setEntities(await HistoryApi.entities());
      } catch {
        // The feed itself is what this page is for. Losing the picker narrows it; it does not
        // break it, and an error state over a working timeline would be a lie.
        setEntities([]);
      }
    })();
  }, []);

  const load = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setFeed(await HistoryApi.feed({
        username: filters.username.trim() || undefined,
        entity: filters.entity || undefined,
        from: dayBoundary(filters.from, false),
        to: dayBoundary(filters.to, true),
        page,
        size: HISTORY_PAGE_SIZE
      }));
    } catch (err) {
      setError(toApiError(err));
      setFeed(null);
    } finally {
      setIsLoading(false);
    }
  }, [filters, page]);

  React.useEffect(() => {
    void load();
  }, [load]);

  /** Any filter change is a new question, so it is asked from the first page. */
  const setFilter = <K extends keyof FeedFilters>(key: K, value: FeedFilters[K]) => {
    setPage(0);
    setFilters(current => ({ ...current, [key]: value }));
  };

  const entries = feed?.entries ?? [];
  const hasFilters = filters.username.trim() !== '' || filters.entity !== '' || filters.from !== '' || filters.to !== '';

  return (
    <React.Fragment>
      <PageSection hasBodyWrapper={false}>
        <Title headingLevel="h1" size="2xl">Activity</Title>
      </PageSection>

      <PageSection hasBodyWrapper={false}>
        <Toolbar id="activity-toolbar" clearAllFilters={() => { setPage(0); setFilters(NO_FILTERS); }}>
          <ToolbarContent>
            <ToolbarGroup variant="filter-group">
              <ToolbarItem>
                <SearchInput
                  aria-label="Changed by"
                  placeholder="Changed by…"
                  value={filters.username}
                  onChange={(_e, value) => setFilter('username', value)}
                  onClear={() => setFilter('username', '')}
                />
              </ToolbarItem>
              <ToolbarItem>
                <EntitySelect
                  entities={entities}
                  selected={filters.entity}
                  onSelect={value => setFilter('entity', value)}
                />
              </ToolbarItem>
              <ToolbarItem>
                <Flex spaceItems={{ default: 'spaceItemsSm' }} alignItems={{ default: 'alignItemsCenter' }}>
                  <FlexItem>
                    <DatePicker
                      aria-label="Changed from"
                      placeholder="From DD/MM/YYYY"
                      value={filters.from}
                      dateFormat={dateFormater}
                      dateParse={ddMMyyyyParse}
                      onChange={(_e, value) => setFilter('from', value)}
                    />
                  </FlexItem>
                  <FlexItem>to</FlexItem>
                  <FlexItem>
                    <DatePicker
                      aria-label="Changed to"
                      placeholder="To DD/MM/YYYY"
                      value={filters.to}
                      dateFormat={dateFormater}
                      dateParse={ddMMyyyyParse}
                      onChange={(_e, value) => setFilter('to', value)}
                    />
                  </FlexItem>
                </Flex>
              </ToolbarItem>
              {hasFilters && (
                <ToolbarItem>
                  <Button variant="link" isInline onClick={() => { setPage(0); setFilters(NO_FILTERS); }}>
                    Clear filters
                  </Button>
                </ToolbarItem>
              )}
            </ToolbarGroup>
          </ToolbarContent>
        </Toolbar>

        {isLoading && !feed && (
          <Bullseye><Spinner aria-label="Loading activity" /></Bullseye>
        )}

        {error && <DataLoadError error={error} entityLabel="the activity feed" onRetry={() => void load()} />}

        {!error && feed && entries.length === 0 && (
          <Bullseye>
            <EmptyState headingLevel="h4" icon={HistoryIcon} titleText="Nothing recorded">
              <EmptyStateBody>
                {hasFilters
                  ? 'No revision matches these filters. A date range is read as whole days in your own time zone.'
                  : 'The service has recorded no changes at all.'}
              </EmptyStateBody>
            </EmptyState>
          </Bullseye>
        )}

        {!error && entries.length > 0 && (
          <React.Fragment>
            <Table aria-label="Activity feed" variant="compact">
              <Thead>
                <Tr>
                  <Th width={20}>When</Th>
                  <Th width={20}>Who</Th>
                  <Th>Touched</Th>
                </Tr>
              </Thead>
              <Tbody>
                {entries.map(entry => <FeedRow key={entry.revision} entry={entry} />)}
              </Tbody>
            </Table>

            <HistoryPager
              page={page}
              count={entries.length}
              hasMore={feed?.hasMore ?? false}
              isLoading={isLoading}
              onPageChange={setPage}
            />
          </React.Fragment>
        )}
      </PageSection>
    </React.Fragment>
  );
};

const FeedRow: React.FunctionComponent<{ entry: FeedEntry }> = ({ entry }) => (
  <Tr>
    <Td dataLabel="When" style={{ whiteSpace: 'nowrap' }}>
      <Tooltip content={`Revision ${entry.revision} — ${ddMMyyyyHHmmTzFormat(entry.changedAt)}`}>
        <span>{ddMMyyyyHHmmFormat(entry.changedAt)}</span>
      </Tooltip>
    </Td>
    <Td dataLabel="Who"><ChangeAuthor username={entry.username} /></Td>
    <Td dataLabel="Touched">
      <Flex spaceItems={{ default: 'spaceItemsXs' }}>
        {entry.touched.map((touched, index) => (
          <FlexItem key={`${touched.type}-${index}`}>
            <TouchedLabel touched={touched} />
          </FlexItem>
        ))}
      </Flex>
    </Td>
  </Tr>
);

/**
 * Ids shown per entity type before the rest are folded away.
 *
 * Not cosmetic: one import run writes a revision touching every skill it refreshed, and the local
 * database has revisions naming forty of them. Listing all forty buries the one line of the row
 * that is worth reading - which type was touched, and by whom - under four rows of near identical
 * chips, and does it for every revision on the page.
 */
const IDS_SHOWN = 6;

/** Unfolds the ids the cap left out. Nothing is lost, it just is not shown until it is asked for. */
const MoreIds: React.FunctionComponent<{ hidden: number; onShowAll(): void }> = ({ hidden, onShowAll }) => {
  if (hidden <= 0) return null;

  return (
    <FlexItem>
      <Button variant="link" isInline onClick={onShowAll}>
        {hidden} more
      </Button>
    </FlexItem>
  );
};

/**
 * One entity type a revision touched, as a chip per record.
 *
 * Three states, and telling them apart is the whole job here:
 *
 * - **A null slug** means the type is audited but either not registered for history or not readable
 *   by this caller - the two are indistinguishable by design - and always arrives with no ids. Shown
 *   as the bare type name, which is all the service is willing to say about it.
 * - **A slug with somewhere to go** is a link, whether that is the record's own page or the table
 *   the record is a row of. See recordPaths.
 * - **A slug with nowhere to go** is most of the registry: the parts of a larger record are
 *   registered so the feed can name a change to one, and they have no resource and no page. Named,
 *   and deliberately not offered as a link.
 */
const TouchedLabel: React.FunctionComponent<{ touched: TouchedEntity }> = ({ touched }) => {
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const [isResolving, setIsResolving] = React.useState(false);
  const [showAll, setShowAll] = React.useState(false);

  const entity = touched.entity;
  const ids = showAll ? touched.ids : touched.ids.slice(0, IDS_SHOWN);
  const hidden = touched.ids.length - ids.length;

  if (!entity) {
    return (
      <Tooltip content="Audited, but either not exposed for history or not yours to read.">
        <Label isCompact variant="outline" color="grey">{touched.type}</Label>
      </Tooltip>
    );
  }

  if (!hasTarget(entity)) {
    return (
      <Flex spaceItems={{ default: 'spaceItemsXs' }}>
        {ids.map(id => (
          <FlexItem key={id}>
            <Tooltip content={`${touched.type} — part of a larger record, with no page of its own.`}>
              <Label isCompact variant="outline" color="grey">{entity} #{id}</Label>
            </Tooltip>
          </FlexItem>
        ))}
        <MoreIds hidden={hidden} onShowAll={() => setShowAll(true)} />
      </Flex>
    );
  }

  const open = async (id: number) => {
    setIsResolving(true);
    try {
      const target = await targetFor(entity, id);
      if (target.kind === 'none') {
        addNotification(
          `There is no page for ${entity} #${id}.`,
          AlertVariant.warning,
          'Nothing to open'
        );
        return;
      }
      navigate(target.path, { state: { backPath: '/activity' } });
    } catch (err) {
      addNotification(toApiError(err).message, AlertVariant.danger, 'Could not open that record');
    } finally {
      setIsResolving(false);
    }
  };

  return (
    <Flex spaceItems={{ default: 'spaceItemsXs' }}>
      {ids.map(id => (
        <FlexItem key={id}>
          <Tooltip content={`${touched.type} — open the record`}>
            <Button
              variant="link"
              isInline
              isDisabled={isResolving}
              onClick={() => void open(id)}
            >
              <Label isCompact color="blue" variant="outline">{entity} #{id}</Label>
            </Button>
          </Tooltip>
        </FlexItem>
      ))}
      <MoreIds hidden={hidden} onShowAll={() => setShowAll(true)} />
    </Flex>
  );
};

const EntitySelect: React.FunctionComponent<{
  entities: AuditableEntityView[];
  selected: string;
  onSelect(entity: string): void;
}> = ({ entities, selected, onSelect }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const toggle = (toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle ref={toggleRef} onClick={() => setIsOpen(open => !open)} isExpanded={isOpen}>
      {selected || 'Any entity'}
    </MenuToggle>
  );

  return (
    <Select
      id="activity-entity-select"
      isOpen={isOpen}
      selected={selected}
      onSelect={(_e, value) => { onSelect(String(value ?? '')); setIsOpen(false); }}
      onOpenChange={setIsOpen}
      toggle={toggle}
      shouldFocusToggleOnSelect
    >
      <SelectOption value="">Any entity</SelectOption>
      {entities.map(entity => (
        <SelectOption key={entity.entity} value={entity.entity}>{entity.entity}</SelectOption>
      ))}
    </Select>
  );
};

const DD_MM_YYYY = /^\d{2}\/\d{2}\/\d{4}$/;

/**
 * A typed pair of calendar days as the instants the endpoint takes, inclusive at both ends: the
 * start of the first day and the last moment of the second, both in the reader's own zone.
 *
 * Instants because the service parses them as such and answers 400 for anything else, never
 * silently ignoring one - which is the right call, since an unfiltered feed returned for a
 * malformed `from` would read as "nothing changed that day". Anything short of a whole date is
 * left out of the request for the same reason: a partial date must not narrow the feed at all.
 */
const dayBoundary = (text: string, endOfDay: boolean): string | undefined => {
  if (!DD_MM_YYYY.test(text)) return undefined;

  const day = ddMMyyyyParse(text);
  if (isNaN(day.getTime())) return undefined;

  return endOfDay
    ? new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59, 999).toISOString()
    : new Date(day.getFullYear(), day.getMonth(), day.getDate(), 0, 0, 0, 0).toISOString();
};

export { Activity };
